import React from 'react';
import './TradeCard.css';

// Use public folder paths for better html2canvas compatibility
const dogeMascot = '/assets/mascots/doge.png';
const pepeMascot = '/assets/mascots/pepe.png';
const logoIcon = '/assets/img/logo_icon.png';

/**
 * Format number to readable format (e.g., 10000000 -> $10.0M)
 */
function formatVolume(dollarSize) {
  if (dollarSize >= 1_000_000_000) {
    return `$${(dollarSize / 1_000_000_000).toFixed(1)}B`;
  }
  if (dollarSize >= 1_000_000) {
    return `$${(dollarSize / 1_000_000).toFixed(1)}M`;
  }
  if (dollarSize >= 1_000) {
    return `$${(dollarSize / 1_000).toFixed(0)}K`;
  }
  return `$${dollarSize.toFixed(0)}`;
}

/**
 * Format price with appropriate decimals
 */
function formatPrice(price) {
  if (price >= 10000) {
    return price.toLocaleString('en-US', { maximumFractionDigits: 0 });
  }
  if (price >= 100) {
    return price.toLocaleString('en-US', { maximumFractionDigits: 2 });
  }
  if (price >= 1) {
    return price.toLocaleString('en-US', { maximumFractionDigits: 4 });
  }
  return price.toLocaleString('en-US', { maximumFractionDigits: 6 });
}

function TradeCard({ trade, markPrice }, ref) {
  // Determine side and color
  // For liquidations: sell = LONG liquidation, buy = SHORT liquidation
  // For whale trades: buy = LONG, sell = SHORT
  const isLongPosition = trade.isLiquidation
    ? trade.side === 'sell'  // Long position liquidated = sold
    : trade.side === 'buy';  // Normal long trade = bought

  // Label: "LIQ LONG" / "LIQ SHORT" for liquidations, "LONG" / "SHORT" for whale trades
  const positionLabel = isLongPosition ? 'LONG' : 'SHORT';
  const sideLabel = trade.isLiquidation ? `LIQ ${positionLabel}` : positionLabel;

  // Simple color rule: LONG = Green + doge, SHORT = Red + pepe
  const useGreenTheme = isLongPosition;

  // Format symbol (BTCUSDT -> BTC/USDT)
  const baseCoin = trade.symbol.replace(/USDT$|USD$/i, '');
  const displaySymbol = `${baseCoin}/USDT`;

  // Theme colors
  const bgGradient = useGreenTheme
    ? 'linear-gradient(135deg, #1a3a2e 0%, #0f2419 100%)'
    : 'linear-gradient(135deg, #4a1515 0%, #2d0f0f 100%)';
  const sideColor = useGreenTheme ? '#37ac66' : '#ac3737';

  // Mascot settings - preserve original aspect ratio
  const mascotSrc = useGreenTheme ? dogeMascot : pepeMascot;
  const mascotStyle = useGreenTheme
    ? { bottom: '-60px', right: '10px', width: '220px', height: 'auto', opacity: 0.35 }
    : { bottom: '-10px', right: '10px', width: '220px', height: 'auto', opacity: 0.25 };

  // Labels
  const priceLabel = trade.isLiquidation ? 'Liq Price' : 'Mark Price';

  return (
    <div
      className="trade-card"
      style={{ background: bgGradient }}
      ref={ref}
    >
      <img
        src={mascotSrc}
        alt="Mascot"
        className="trade-card-mascot"
        style={mascotStyle}
      />

      <div className="trade-card-header">
        <div className="trade-card-via">via: tradermap.io</div>
        <div className="trade-card-logo">
          <img src={logoIcon} alt="TraderMap" />
        </div>
      </div>

      <div className="trade-card-content">
        <div className="trade-card-type-pair">
          <span className="trade-card-type" style={{ color: sideColor }}>{sideLabel}</span>
          <span className="trade-card-separator">|</span>
          <span className="trade-card-pair">{displaySymbol}</span>
        </div>

        <div className="trade-card-price" style={{ color: sideColor }}>
          {formatVolume(trade.dollarSize)}
        </div>

        <div className="trade-card-details">
          {!trade.isLiquidation && (
            <div className="trade-card-detail-item">
              <div className="trade-card-detail-label">Entry Price</div>
              <div className="trade-card-detail-value">{formatPrice(trade.price)}</div>
            </div>
          )}
          <div className="trade-card-detail-item">
            <div className="trade-card-detail-label">{priceLabel}</div>
            <div className="trade-card-detail-value">
              {/* Liquidations: show the liq event price from the trade record.
                  Whale trades: show the live mark price (falling back to trade.price). */}
              {formatPrice(trade.isLiquidation ? trade.price : (markPrice || trade.price))}
            </div>
          </div>
        </div>

        <div className="trade-card-exchange-section">
          <div className="trade-card-exchange-label">Exchange</div>
          <div className="trade-card-exchange-value">{trade.exchange.replace('_', ' ')}</div>
        </div>
      </div>
    </div>
  );
}

export default React.forwardRef(TradeCard);
